import { Fragment, useMemo } from 'react';
import type { NdaSection } from '../../api/client';
import { cn } from '@/lib/utils';

/**
 * The NDA, with the signer's details merged in live as they type.
 *
 * The backend sends plain text carrying `{{field}}` markers rather than HTML,
 * so nothing from the document is ever injected into the page — we split on
 * the markers here and render each substitution as its own highlighted span.
 * Unfilled fields show the field name greyed out, so the signer can see what
 * is still missing without hunting for it.
 */

export type MergeValues = Record<string, string>;

const PLACEHOLDER_LABELS: Record<string, string> = {
  date: '[Date]',
  company: '[Company]',
  signer_name: '[Signer name]',
  signer_title: '[Title]',
  signer_email: '[Email]',
};

const MERGE_PATTERN = /\{\{(\w+)\}\}/g;

function MergedText({ text, values }: { text: string; values: MergeValues }) {
  const parts = useMemo(() => {
    const out: Array<{ key: string; literal?: string; field?: string }> = [];
    let cursor = 0;
    for (const match of text.matchAll(MERGE_PATTERN)) {
      const at = match.index ?? 0;
      if (at > cursor) out.push({ key: `t${cursor}`, literal: text.slice(cursor, at) });
      out.push({ key: `f${at}`, field: match[1] });
      cursor = at + match[0].length;
    }
    if (cursor < text.length) out.push({ key: `t${cursor}`, literal: text.slice(cursor) });
    return out;
  }, [text]);

  return (
    <>
      {parts.map((part) => {
        if (part.literal !== undefined) return <Fragment key={part.key}>{part.literal}</Fragment>;
        const field = part.field as string;
        const value = (values[field] || '').trim();
        return (
          <span key={part.key} className={cn('ob-merge', !value && 'ob-merge--empty')}>
            {value || PLACEHOLDER_LABELS[field] || `[${field}]`}
          </span>
        );
      })}
    </>
  );
}

export default function NdaDocument({
  sections,
  values,
  className,
}: {
  sections: NdaSection[];
  values: MergeValues;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'ob-nda overflow-y-auto rounded-xl border bg-card px-4 py-4 text-xs leading-relaxed text-muted-foreground',
        className,
      )}
    >
      {sections.map((section, i) => (
        <section key={section.heading} className={i === 0 ? '' : 'mt-3'}>
          <h4 className="mb-1.5 text-[12.5px] font-bold tracking-tight text-foreground">
            {section.heading}
          </h4>
          {section.blocks.map((block, j) =>
            block.type === 'ul' ? (
              <ul key={j} className="mb-2 flex list-disc flex-col gap-1 pl-[18px] marker:text-border">
                {block.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : (
              <p key={j} className="mb-2 text-pretty">
                <MergedText text={block.text} values={values} />
              </p>
            ),
          )}
        </section>
      ))}
    </div>
  );
}
